import { type FC, type JSX, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type OfferV11,
  type PimProductWithVariantsV1,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { Link } from 'base-ui'

import { useOpenableControls } from '../../../generic-hooks/useOpenableControls/useOpenableControls'
import { useProductDeliveryPrices } from '../../../product-page/components/ProductBuyBox/hooks/useProductDeliveryPrices'
import {
  determineFreeShipping,
  getAmount,
} from '../../../product-page/components/ProductBuyBox/ProductBuyBoxStockStatusAndDeliveryInfo/ProductBuyBoxDeliveryInfo/ProductBuyBoxDeliveryInfo'
import { ProductDeliveryInfoDialog } from '../../../product-page/components/ProductDeliveryInfoDialog/ProductDeliveryInfoDialog'
import { useProductPageContext } from '../../../product-page/components/productPageContext'
import { productAccessorStockStatusIsAvailable } from '../../../product-page/model/productAccessor/productAccessorStockStatusIsAvailable'

export const ExperimentOwnBrandsProductDeliveryInfo: FC<{
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  isMarketplace: boolean
  offer: OfferV11 | undefined
  offerId: string | null
  product: PimProductWithVariantsV1
}> = ({ currentPrescriptionType, isMarketplace, offer, offerId, product }) => {
  const {
    productTemplate,
    shipping: { marketplaceDeliveryCosts },
  } = useProductPageContext()

  const { freeShippingThreshold, isFreeShippingSae, shippingPriceMarketplace } =
    useProductDeliveryPrices({
      currentPrescriptionType,
      offerId,
      product,
      productTemplate,
    })

  const { handleClose, handleOpen, isOpen } = useOpenableControls(false)

  if (!productAccessorStockStatusIsAvailable(product) || !offer) {
    return null
  }

  if (
    determineFreeShipping(
      isMarketplace,
      isFreeShippingSae,
      marketplaceDeliveryCosts,
    )
  ) {
    return (
      <span className="font-medium text-dark-success-medium">
        <FormattedMessage id="buyBox.link.label.aboveFreeShippingLimit" />
      </span>
    )
  }

  const linkToModal = (text: ReactNode): JSX.Element => (
    <Link<'button'>
      color="inherit"
      onClick={(): void => {
        handleOpen()
      }}
      slots={{ root: 'button' }}
      type="button"
    >
      {text}
    </Link>
  )

  return (
    <span>
      {isMarketplace ? (
        <FormattedMessage
          id="buyBox.link.label.belowFreeShippingLimitWithLinkMp_ho"
          values={{
            amount: getAmount(
              shippingPriceMarketplace ?? 0,
              freeShippingThreshold.currency,
            ),
            linkToModal,
          }}
        />
      ) : (
        typeof freeShippingThreshold.amount === 'number' && (
          <FormattedMessage
            id="buyBox.link.label.belowFreeShippingLimitWithLink_ho"
            values={{
              amount: getAmount(
                freeShippingThreshold.amount,
                freeShippingThreshold.currency,
              ),
              linkToModal,
            }}
          />
        )
      )}
      <ProductDeliveryInfoDialog isOpen={isOpen} onClose={handleClose} />
    </span>
  )
}
